import React, { useState, useEffect, useRef } from 'react';
import ItemSummaryCard from './ItemSummary';
import { API_BASE_URL } from '../urldata.js';

const SimilarItemsBar = ({ itemTitle, currentItemId }) => {
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const scrollRef = useRef(null);

    useEffect(() => {
        const fetchSimilarItems = async () => {
            if (!itemTitle) return;

            setIsLoading(true);
            setError(null);
            try {
                // Use complete title for searching similar items
                const keywords = itemTitle;
                const response = await fetch(`${API_BASE_URL}/tapi/search?q=${encodeURIComponent(keywords)}&page=1`);

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                const itemsList = Array.isArray(data) ? data : (data.data || data.results || []);

                // Filter out the current item and limit to 10
                const filteredItems = itemsList.filter(item => String(item.id) !== String(currentItemId)).slice(0, 10);

                setItems(filteredItems);
            } catch (err) {
                console.error("Failed to fetch similar items:", err);
                setError(err.message || "Failed to load similar items.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchSimilarItems();
    }, [itemTitle, currentItemId]);

    const scroll = (direction) => {
        if (scrollRef.current) {
            const container = scrollRef.current;
            const scrollAmount = container.clientWidth * 0.8;
            const target = direction === 'left' ? -scrollAmount : scrollAmount;
            const start = container.scrollLeft;
            const duration = 1200;
            let startTime = null;

            const easeInOutQuad = (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

            const animate = (timestamp) => {
                if (!startTime) startTime = timestamp;
                const progress = timestamp - startTime;
                const fraction = Math.min(progress / duration, 1);
                const ease = easeInOutQuad(fraction);

                container.scrollLeft = start + target * ease;

                if (progress < duration) {
                    requestAnimationFrame(animate);
                }
            };

            requestAnimationFrame(animate);
        }
    };

    if (isLoading || items.length === 0) return null;

    return (
        <div className="category-section-wrapper highlighted-bar mt-5">
            <div className="container-fluid px-4">
                <div className="category-header">
                    <div className="d-flex align-items-center">
                        <h2 className="category-title-link mb-0" style={{ cursor: 'default' }}>
                            Similar Items
                        </h2>
                    </div>
                    <div className="category-controls">
                        <button className="scroll-btn" onClick={() => scroll('left')} aria-label="Scroll left">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-chevron-left" viewBox="0 0 16 16">
                                <path fillRule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z" />
                            </svg>
                        </button>
                        <button className="scroll-btn" onClick={() => scroll('right')} aria-label="Scroll right">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-chevron-right" viewBox="0 0 16 16">
                                <path fillRule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z" />
                            </svg>
                        </button>
                    </div>
                </div>
                <div className="category-items-row" ref={scrollRef}>
                    {items.map(item => (
                        <div key={item.id} style={{
                            flex: '0 0 auto',
                            width: '240px',
                            marginRight: '1rem',
                            display: 'flex',
                            scrollSnapAlign: 'start'
                        }}>
                            <ItemSummaryCard item={item} showDescription={false} />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default SimilarItemsBar;
